import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSubscriptionPlans, subscribeToPlan } from '../../api/services/billingService';
import { getAuthToken, removeAuthToken, removeRefreshToken } from '../../api/services/authService';
import './SubscriptionPlans.css';

const defaultForm = {
  planId: '',
  paymentMethod: 'MPESA',
  paymentReference: '',
};

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [pageMessage, setPageMessage] = useState({ type: '', text: '' });
  const [checkoutMessage, setCheckoutMessage] = useState({ type: '', text: '' });
  const returnTo = location.state?.returnTo || '/';
  const submittingRef = useRef(submitting);
  submittingRef.current = submitting;

  useEffect(() => {
    if (!getAuthToken()) {
      navigate('/login', { replace: true });
      return;
    }

    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await getSubscriptionPlans();
        const payload = response?.data || response;
        const planList = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
        setPlans(planList);
      } catch (error) {
        setPageMessage({ type: 'error', text: error.message || 'Unable to load plans right now.' });
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [navigate]);

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'KES',
      maximumFractionDigits: 0,
    }).format(Number(value || 0));

  const closeCheckout = useCallback(() => {
    if (submittingRef.current) return;
    setCheckoutOpen(false);
    setCheckoutMessage({ type: '', text: '' });
    setForm((prev) => ({ ...prev, paymentReference: '' }));
  }, []);

  useEffect(() => {
    if (!checkoutOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeCheckout();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [checkoutOpen, closeCheckout]);

  const handleSelectPlan = (planId) => {
    setSelectedPlanId(planId);
    setForm((prev) => ({ ...prev, planId, paymentReference: '' }));
    setCheckoutMessage({ type: '', text: '' });
    setPageMessage({ type: '', text: '' });
    setCheckoutOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.planId) {
      setCheckoutMessage({ type: 'error', text: 'Please choose a plan first.' });
      return;
    }

    if (!form.paymentReference.trim()) {
      setCheckoutMessage({ type: 'error', text: 'Please enter the payment reference.' });
      return;
    }

    try {
      setSubmitting(true);
      setCheckoutMessage({ type: '', text: '' });
      const response = await subscribeToPlan({
        planId: form.planId,
        paymentMethod: form.paymentMethod,
        paymentReference: form.paymentReference.trim(),
      });

      if (response?.status === 'success' || response?.success === true) {
        localStorage.removeItem('tenantscore_subscription_state');
        setPageMessage({ type: 'success', text: response.message || 'Subscription activated successfully.' });
        setCheckoutOpen(false);
        navigate(returnTo || '/', { replace: true });
      } else {
        setCheckoutMessage({ type: 'error', text: response?.message || 'Subscription could not be completed.' });
      }
    } catch (error) {
      setCheckoutMessage({ type: 'error', text: error.message || 'Subscription could not be completed.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    removeAuthToken();
    removeRefreshToken();
    navigate('/login', { replace: true });
  };

  return (
    <div className="subscription-page">
      <div className="subscription-shell">
        <header className="subscription-hero">
          <div className="subscription-hero__content">
            <p className="eyebrow">Subscription access</p>
            <h1>Pick the plan that fits your property business</h1>
            <p className="hero-copy">
              Landlords and short-stay hosts need an active subscription to unlock the full platform experience.
              Compare plans below, then complete checkout when you are ready.
            </p>
          </div>
          <button type="button" className="ghost-button" onClick={handleLogout}>
            Logout
          </button>
        </header>

        <section className="subscription-steps" aria-label="How subscription works">
          <article className="step-card">
            <span className="step-card__number">1</span>
            <div>
              <h2>Choose a plan</h2>
              <p>Review pricing, billing cycle, and what each plan includes.</p>
            </div>
          </article>
          <article className="step-card">
            <span className="step-card__number">2</span>
            <div>
              <h2>Pay with M-PESA</h2>
              <p>Send payment and enter your M-PESA reference in checkout.</p>
            </div>
          </article>
          <article className="step-card">
            <span className="step-card__number">3</span>
            <div>
              <h2>Start managing</h2>
              <p>Your subscription activates instantly and you can continue to your dashboard.</p>
            </div>
          </article>
        </section>

        {pageMessage.text ? (
          <div className={`subscription-alert ${pageMessage.type === 'success' ? 'success' : 'error'}`}>
            {pageMessage.text}
          </div>
        ) : null}

        {loading ? (
          <div className="subscription-loading">
            <span className="subscription-loading__spinner" aria-hidden="true" />
            <p>Loading plans...</p>
          </div>
        ) : plans.length === 0 ? (
          <div className="subscription-empty">
            <h2>No plans are available yet</h2>
            <p>There are currently no subscription plans to display. Please try again later.</p>
          </div>
        ) : (
          <section className="plan-grid" aria-label="Available subscription plans">
            {plans.map((plan) => {
              const hasDiscount = Number(plan.discount || 0) > 0;
              const isSelected = selectedPlanId === plan.id;

              return (
                <article
                  key={plan.id}
                  className={`plan-card ${isSelected ? 'plan-card--selected' : ''}`}
                >
                  {hasDiscount ? <span className="plan-card__tag">Save {Number(plan.discount)}%</span> : null}

                  <div className="plan-card__top">
                    <p className="plan-badge">{plan.billingCycle || 'MONTHLY'}</p>
                    <h3>{plan.title}</h3>
                    <p className="plan-description">{plan.description}</p>
                  </div>

                  <div className="plan-card__pricing">
                    <span className="plan-price">{formatCurrency(plan.effectiveAmount ?? plan.amount)}</span>
                    {hasDiscount ? (
                      <span className="plan-price--original">{formatCurrency(plan.amount)}</span>
                    ) : null}
                    <span className="plan-price__cycle">per {plan.durationDays || 30} days</span>
                  </div>

                  <ul className="plan-meta">
                    <li>
                      <span>Billing cycle</span>
                      <strong>{plan.billingCycle || 'MONTHLY'}</strong>
                    </li>
                    <li>
                      <span>Duration</span>
                      <strong>{plan.durationDays || 30} days</strong>
                    </li>
                    {hasDiscount ? (
                      <li>
                        <span>You save</span>
                        <strong>{Number(plan.discount)}%</strong>
                      </li>
                    ) : null}
                  </ul>

                  <button type="button" className="select-button" onClick={() => handleSelectPlan(plan.id)}>
                    Select plan
                  </button>
                </article>
              );
            })}
          </section>
        )}
      </div>

      {checkoutOpen && selectedPlan ? (
        <div
          className="checkout-modal-overlay"
          role="presentation"
          onClick={closeCheckout}
        >
          <div
            className="checkout-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="checkout-modal__header">
              <div>
                <p className="eyebrow">Checkout</p>
                <h2 id="checkout-modal-title">{selectedPlan.title}</h2>
                <p className="checkout-copy">
                  Complete payment with your M-PESA reference to activate your subscription instantly.
                </p>
              </div>
              <button
                type="button"
                className="checkout-modal__close"
                onClick={closeCheckout}
                aria-label="Close checkout"
                disabled={submitting}
              >
                ×
              </button>
            </div>

            {checkoutMessage.text ? (
              <div className={`subscription-alert checkout-alert ${checkoutMessage.type === 'success' ? 'success' : 'error'}`}>
                {checkoutMessage.text}
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="checkout-form">
              <div className="checkout-plan-summary">
                <div>
                  <span>Plan</span>
                  <strong>{selectedPlan.title}</strong>
                </div>
                <div>
                  <span>Amount due</span>
                  <strong>{formatCurrency(selectedPlan.effectiveAmount ?? selectedPlan.amount)}</strong>
                </div>
                <div>
                  <span>Duration</span>
                  <strong>{selectedPlan.durationDays || 30} days</strong>
                </div>
              </div>

              <label className="field">
                <span>Payment method</span>
                <select
                  value={form.paymentMethod}
                  onChange={(e) => setForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  <option value="MPESA">M-PESA</option>
                </select>
              </label>

              <label className="field">
                <span>Payment reference</span>
                <input
                  type="text"
                  value={form.paymentReference}
                  onChange={(e) => setForm((prev) => ({ ...prev, paymentReference: e.target.value }))}
                  placeholder="e.g. QFGH7K2P9X"
                  autoFocus
                />
              </label>

              <div className="checkout-modal__actions">
                <button type="button" className="ghost-button" onClick={closeCheckout} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={submitting}>
                  {submitting ? 'Subscribing...' : 'Subscribe now'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SubscriptionPlans;
